import React from 'react';
import { cn } from '@/utils/cn';
import { useNavigate } from 'react-router-dom';
import { useCart, CartGroup, groupCartItems } from '@/Context/CartContext';
import { cartLabels, features } from '@/data/content';
import { trackEvent } from '@/Services/analytics';
import Button from '@/components/ui/Button';
import { TrashIcon, CloseIcon, ArrowRightIcon } from '@/components/ui/Icons';
import { formatPrice, formatGroupDate as sharedFormatGroupDate } from '@/Services/format';

const CartDrawer: React.FC = () => {
    const {
        cartItems,
        isCartOpen,
        toggleCart,
        removeFromCart,
        updateQuantity,
        getCartTotal,
        highlightedItemId,
        invalidItemIds,
    } = useCart();

    const navigate = useNavigate();

    const grouped = groupCartItems(cartItems);

    const formatGroupDate = (dateStr: string | undefined): string =>
        sharedFormatGroupDate(dateStr, cartLabels.group_no_date, cartLabels.group_date_prefix);

    const handleCheckout = () => {
        trackEvent('cart_checkout_clicked', { item_count: cartItems.length, cart_total: getCartTotal() });
        toggleCart();
        navigate('/checkout');
    };

    if (!isCartOpen) return null;

    const removeMenu = (indices: number[]) => {
        const sorted = [...indices].sort((a, b) => b - a);
        sorted.forEach((i) => removeFromCart(i));
    };

    const renderInvalidWarning = (mg: { items: { item: (typeof cartItems)[number]; index: number }[] }) => {
        const hasInvalid = mg.items.some(({ item }) => invalidItemIds.has(item.id));
        if (!hasInvalid) return null;
        const menuIndices = mg.items.map((i) => i.index);
        return (
            <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                    {cartLabels.item_unavailable_warning}
                    <Button
                        variant="ghost"
                        onClick={() => removeMenu(menuIndices)}
                        className="underline ml-1 font-bold"
                    >
                        {cartLabels.item_unavailable_remove}
                    </Button>
                </p>
            </div>
        );
    };

    const renderMenuGroupNames = (mg: { items: { item: (typeof cartItems)[number]; index: number }[] }) => {
        const byGroup = new Map<string, string[]>();
        mg.items.forEach(({ item }) => {
            const gName = (item.menu_group_name as string) || '';
            const existing = byGroup.get(gName) ?? [];
            existing.push(item.name);
            byGroup.set(gName, existing);
        });
        const entries = Array.from(byGroup.entries());
        const hasGroups = entries.some(([name]) => name);
        return entries.map(([groupName, names]) => (
            <div key={groupName} className="py-1.5">
                {hasGroups && groupName && (
                    <span className="block text-[10px] italic text-app-muted mb-0.5">{groupName}</span>
                )}
                {names.map((name, ni) => (
                    <span
                        key={name + ni}
                        className={cn('block text-xs text-app-text', hasGroups && groupName && 'pl-3')}
                    >
                        · {name}
                    </span>
                ))}
            </div>
        ));
    };

    const renderCartItemList = (group: CartGroup): React.ReactElement[] => {
        const menuGroups = new Map<
            string,
            { items: { item: (typeof cartItems)[number]; index: number }[]; menuName: string }
        >();
        const standalone: { item: (typeof cartItems)[number]; index: number }[] = [];

        group.items.forEach((entry) => {
            const mid = entry.item.menu_instance_id || entry.item.menu_id;
            if (mid) {
                const key = String(mid);
                if (!menuGroups.has(key)) {
                    menuGroups.set(key, {
                        items: [],
                        menuName: (entry.item.menu_name as string) ?? 'Menu',
                    });
                }
                menuGroups.get(key)!.items.push(entry);
            } else {
                standalone.push(entry);
            }
        });

        const elements: React.ReactElement[] = [];

        menuGroups.forEach((mg, key) => {
            const menuQuantity = mg.items[0]?.item.quantity || 1;
            const menuPrice = mg.items.reduce((sum, { item }) => sum + item.price, 0);

            const handleMenuQty = (newQty: number) => {
                if (newQty < 1) return;
                mg.items.forEach(({ index }) => updateQuantity(index, newQty));
            };

            elements.push(
                <li
                    key={`menu-${key}`}
                    className="mb-4 bg-app-surface border border-app-border rounded-2xl shadow-sm overflow-hidden"
                >
                    <div className="flex items-center justify-between p-3 bg-app-bg border-b border-app-border">
                        <div>
                            <span className="text-sm font-bold text-app-primary uppercase tracking-wider block leading-tight">
                                {mg.menuName}
                            </span>
                            <span className="text-app-text font-semibold text-sm">{formatPrice(menuPrice)}</span>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center bg-app-surface border border-app-border rounded-lg p-0.5">
                                <Button
                                    variant="ghost"
                                    onClick={() => handleMenuQty(menuQuantity - 1)}
                                    aria-label={cartLabels.decrease_qty_label}
                                    className="!p-0 w-7 h-7 rounded-md text-lg leading-none"
                                >
                                    &#8722;
                                </Button>
                                <span className="w-6 text-center font-bold text-sm text-app-text">{menuQuantity}</span>
                                <Button
                                    variant="ghost"
                                    onClick={() => handleMenuQty(menuQuantity + 1)}
                                    aria-label={cartLabels.increase_qty_label}
                                    className="!p-0 w-7 h-7 rounded-md text-lg leading-none"
                                >
                                    +
                                </Button>
                            </div>

                            <div className="flex flex-col items-end gap-1">
                                <Button
                                    variant="ghost"
                                    onClick={() => removeMenu(mg.items.map((i) => i.index))}
                                    aria-label={cartLabels.remove_label}
                                    className="!p-1.5 text-app-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                </Button>
                                <span className="font-bold text-app-text text-sm">
                                    {formatPrice(menuPrice * menuQuantity)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="divide-y divide-app-border/50 px-4 py-2">
                        {renderMenuGroupNames(mg)}
                        {renderInvalidWarning(mg)}
                    </div>
                </li>
            );
        });

        standalone.forEach(({ item, index }) => {
            elements.push(
                <li
                    key={item.id}
                    className={cn(
                        'flex gap-4 p-4 bg-app-surface border border-app-border rounded-2xl shadow-sm hover:shadow-md transition-shadow',
                        highlightedItemId === item.id && 'ring-2 ring-app-primary/30'
                    )}
                >
                    <div className="flex-1 flex flex-col justify-between">
                        <div>
                            <h6 className="font-bold text-app-text mb-1 leading-tight">
                                {item.name}
                                {item.portion_label && (
                                    <span className="text-app-muted ml-1 font-normal text-sm opacity-70">
                                        ({item.portion_label})
                                    </span>
                                )}
                            </h6>
                            <p className="text-app-primary font-semibold text-sm">{formatPrice(item.price)}</p>
                            {item.weight_percentage != null && (
                                <span className="text-xs font-medium text-app-muted bg-app-border/30 px-2 py-0.5 rounded-full inline-block">
                                    {item.weight_percentage}%
                                </span>
                            )}
                            {invalidItemIds.has(item.id) && (
                                <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-2">
                                    <p className="text-xs text-amber-700 dark:text-amber-300">
                                        {cartLabels.item_unavailable_warning}
                                        <Button
                                            variant="ghost"
                                            onClick={() => removeFromCart(index)}
                                            className="underline ml-1 font-bold"
                                        >
                                            {cartLabels.item_unavailable_remove}
                                        </Button>
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-3 mt-3">
                            <div className="flex items-center bg-app-bg border border-app-border rounded-lg p-0.5">
                                <Button
                                    variant="ghost"
                                    onClick={() => updateQuantity(index, item.quantity - 1)}
                                    aria-label={cartLabels.decrease_qty_label}
                                    className="!p-0 w-8 h-8 rounded-md text-lg leading-none"
                                >
                                    &#8722;
                                </Button>
                                <span
                                    className={cn(
                                        'w-8 text-center font-bold text-sm transition-colors duration-500',
                                        highlightedItemId === item.id ? 'text-app-primary scale-110' : 'text-app-text'
                                    )}
                                >
                                    {item.quantity}
                                </span>
                                <Button
                                    variant="ghost"
                                    onClick={() => updateQuantity(index, item.quantity + 1)}
                                    aria-label={cartLabels.increase_qty_label}
                                    className="!p-0 w-8 h-8 rounded-md text-lg leading-none"
                                >
                                    +
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col items-end justify-between">
                        <Button
                            variant="ghost"
                            onClick={() => removeFromCart(index)}
                            aria-label={cartLabels.remove_label}
                            className="!p-2 text-app-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                            <TrashIcon className="w-5 h-5" />
                        </Button>

                        <span className="font-bold text-app-text">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                </li>
            );
        });

        return elements;
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1040] transition-opacity duration-300"
                onClick={toggleCart}
                aria-hidden="true"
            ></div>

            {/* Drawer */}
            <div
                className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-app-surface dark:bg-gray-900 shadow-2xl z-[1045] flex flex-col animate-in slide-in-from-right duration-300 border-l border-app-border"
                tabIndex={-1}
                role="dialog"
                aria-label={cartLabels.title}
            >
                <div className="flex items-center justify-between p-5 border-b border-app-border">
                    <h5 className="text-xl font-bold text-app-text m-0">{cartLabels.title}</h5>
                    <Button
                        variant="ghost"
                        onClick={toggleCart}
                        aria-label={cartLabels.close_label}
                        className="!p-2 -mr-2 rounded-lg"
                    >
                        <CloseIcon className="w-6 h-6" />
                    </Button>
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                    {cartItems.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                            <div className="w-24 h-24 bg-app-primary/10 rounded-full flex items-center justify-center mb-2">
                                <span className="text-5xl" aria-hidden="true">
                                    🛒
                                </span>
                            </div>
                            <p className="text-app-muted font-medium">{cartLabels.empty_message}</p>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                    toggleCart();
                                    navigate('/menu');
                                }}
                                className="mt-4"
                            >
                                {cartLabels.view_menu}
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {grouped.map((group) => (
                                <div key={group.key}>
                                    {/* Group header */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="flex-1 h-px bg-app-border/40" />
                                        <span className="text-xs font-bold text-app-muted uppercase tracking-wider whitespace-nowrap px-2">
                                            {formatGroupDate(group.date)}
                                        </span>
                                        <div className="flex-1 h-px bg-app-border/40" />
                                    </div>

                                    {/* Items in this group */}
                                    <ul className="space-y-3">{renderCartItemList(group)}</ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {cartItems.length > 0 && (
                    <div className="p-5 border-t border-app-border bg-app-bg">
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-app-muted font-medium text-lg">{cartLabels.total}</span>
                            <span className="text-2xl font-black text-app-text">{formatPrice(getCartTotal())}</span>
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                    toggleCart();
                                    navigate('/menu');
                                }}
                                className="shrink-0"
                            >
                                {cartLabels.menu_button}
                            </Button>
                            {features.order && (
                                <Button variant="primary" size="lg" onClick={handleCheckout} className="flex-1 gap-2">
                                    <span>{cartLabels.checkout}</span>
                                    <ArrowRightIcon />
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default CartDrawer;
